"use client";
import {
  Accordion,
  AccordionTrigger,
  AccordionItem,
  AccordionContent,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import Title from "@/components/ui/title";
import CategorySelector from "./CategorySelector";
import { Search, X, RotateCw, Download } from "lucide-react";
import { useScopedI18n } from "@/messages/client";
import { useCallback, useEffect, useMemo, useState } from "react";
import useSWRMutation from "swr/mutation";
import { fetchCollectionsData, type CollectionQuerys } from "@/lib/fetchData";
import Loading from "@/components/ui/loading";
import { concat, without } from "lodash-es";
import { cn, getFileDownloadAdress, getSuffixByFileName } from "@/lib/utils";
import { useLang } from "@/hooks/useSiteInfo";
import { useUser } from "@/components/clients/LocaleToStorage";
import { useDebounce } from "ahooks";
import RouterLink from "@/components/ui/router-link";
import toast from "react-hot-toast";
import Pagination from '@/components/blocks/GlobalSearch/search-pagination'
import useLangData from "@/hooks/useLangData";
import { defaultLang } from "@/lib/getSite";

const filterTypes = {
  document: "DOCUMENT",
  application: "APPLICATION",
  product: "PRODUCT",
};

type DirectusFile = {
  id: string;
  filename_disk: string;
  filename_download: string;
  title: string;
};

type ProductTechfile = {
  id: number;
  slug: string;
  name: string;
  category: {
    id: number;
    name: string;
    lang?: number;
    lang_data?: {
      name: string;
      lang: number;
    }[];
  };
  datasheet1_text?: string;
  datasheet1_file?: DirectusFile;
  datasheet2_text?: string;
  datasheet2_file?: DirectusFile;
  datasheet3_text?: string;
  datasheet3_file?: DirectusFile;
  // datasheet4_file?: DirectusFile;
  // datasheet5_file?: DirectusFile;
  application_file?: DirectusFile;
  evm_file?: DirectusFile;
};

export default function TechFileDownloadClient(props: {
  applicationCategoryData?: {
    id: number;
    slug: string;
    name: string;
  }[];
  productCategoryData?: {
    id: number;
    slug?: string;
    name: string;
    children?: {
      id: number;
      slug: string;
      name: string;
    }[];
  }[];
}) {
  const lang = useLang();
  const getLangData = useLangData();
  const { isLoading: userInfoLoading, user } = useUser();
  const t = useScopedI18n("资料下载");
  const tCommon = useScopedI18n("通用");
  const [isMobile, setIsMobile] = useState<boolean>();
  const [documentCategorys, setDocumentCategorys] = useState<number[]>([]);
  const [applicationCategorys, setApplicationCategorys] = useState<number[]>(
    []
  );
  const [productCategorys, setProductCategorys] = useState<number[]>([]);
  const [name, setName] = useState("");
  const [searchName, setSearchName] = useState("");
  const [page, setPage] = useState(1);

  const documentCategoryData = useMemo(
    () => [
      { id: 1, name: t("产品数据手册") },
      { id: 2, name: t("产品应用手册") },
      { id: 3, name: t("EVM用户指南") },
    ],
    []
  );

  const productChildrenCategorys = useMemo(() => {
    const categorys: {
      id: number;
      name: string;
    }[] = [];

    props.productCategoryData?.forEach((el) => {
      if (el.children) {
        categorys.push(
          ...el.children.map((item) => ({
            id: item.id,
            name: item.name,
          }))
        );
      }
    });

    return categorys;
  }, [props.productCategoryData]);

  const categoryFilters = useMemo(
    () =>
      concat(
        documentCategoryData
          .filter((el) => documentCategorys.includes(el.id))
          .map((el) => ({ ...el, type: filterTypes.document })),
        props.applicationCategoryData
          ?.filter((el) => applicationCategorys.includes(el.id))
          .map((el) => ({ ...el, type: filterTypes.application })) ?? [],
        productChildrenCategorys
          .filter((el) => productCategorys.includes(el.id))
          .map((el) => ({ ...el, type: filterTypes.product })) ?? []
      ),
    [
      documentCategoryData,
      props.applicationCategoryData,
      productChildrenCategorys,
      documentCategorys,
      applicationCategorys,
      productCategorys,
    ]
  );

  const debounceFilters = useDebounce(categoryFilters, { wait: 500 });

  const reset = useCallback(() => {
    setDocumentCategorys([]);
    setApplicationCategorys([]);
    setProductCategorys([]);
    setSearchName("");
    setName("");
    setPage(1);
  }, []);

  const resetAll = useCallback(() => {
    reset();
    setName("");
    setPage(1);
  }, [reset]);

  const removeFilter = useCallback(
    (id: number, type: string) => {
      switch (type) {
        case filterTypes.document: {
          setDocumentCategorys(without(documentCategorys, id));
          break;
        }
        case filterTypes.application: {
          setApplicationCategorys(without(applicationCategorys, id));
          break;
        }
        case filterTypes.product: {
          setProductCategorys(without(productCategorys, id));
          break;
        }
        default: {
        }
      }
    },
    [documentCategorys, productCategorys, applicationCategorys]
  );

  const showDataseeht = useMemo(
    () => documentCategorys.includes(1),
    [documentCategorys]
  );

  const showApplication = useMemo(
    () => documentCategorys.includes(2),
    [documentCategorys]
  );

  const showEvm = useMemo(
    () => documentCategorys.includes(3),
    [documentCategorys]
  );

  const {
    trigger,
    isMutating: loading,
    data: result,
  } = useSWRMutation(
    [
      "/items/product",
      user?.role,
      documentCategorys,
      applicationCategorys,
      productCategorys,
      name,
      page,
    ],
    async (arg) => {
      const [
        ,
        role,
        documentCategoryFilters,
        applicationCategoryFilters,
        productCategoryFilters,
        nameFilter,
        pageFilter,
      ] = arg;
      const querys: CollectionQuerys = [];

      const documentQuerys: {
        key: string | string[];
        value: string;
      }[] = [];
      const { level } = role ?? {};

      /** 未设置文档类型筛选或筛选了产品数据手册 */
      if (
        !documentCategoryFilters.length ||
        documentCategoryFilters.includes(1)
      ) {
        if (!level || level === 1) {
          documentQuerys.push({
            key: ["datasheet1_file", "_nnull"],
            value: "true",
          });
        }
        if (!level || level === 2) {
          documentQuerys.push({
            key: ["datasheet2_file", "_nnull"],
            value: "true",
          });
        }
        if (!level || level === 3) {
          documentQuerys.push({
            key: ["datasheet3_file", "_nnull"],
            value: "true",
          });
        }
        // if (!level || level === 4) {
        //   documentQuerys.push({
        //     key: ["datasheet1_file", "_nnull"],
        //     value: "true",
        //   });
        // }
        // if (!level || level === 5) {
        //   documentQuerys.push({
        //     key: ["datasheet5_file", "_nnull"],
        //     value: "true",
        //   });
        // }
      }

      /** 未设置文档类型筛选或筛选了产品应用手册 */
      if (
        !documentCategoryFilters.length ||
        documentCategoryFilters.includes(2)
      ) {
        documentQuerys.push({
          key: ["application_file", "_nnull"],
          value: "true",
        });
      }

      /** 未设置文档类型筛选或筛选了EVM用户指南 */
      if (
        !documentCategoryFilters.length ||
        documentCategoryFilters.includes(3)
      ) {
        documentQuerys.push({
          key: ["evm_file", "_nnull"],
          value: "true",
        });
      }

      querys.push({
        key: "_or",
        filter: documentQuerys,
      });

      if (applicationCategoryFilters.length) {
        querys.push({
          key: ["application_category", "id"],
          filter: {
            _in: applicationCategoryFilters,
          },
        });
      }

      if (productCategoryFilters.length) {
        querys.push({
          key: ["category", "parent", "id"],
          filter: {
            _in: productCategoryFilters,
          },
        });
      }

      if (nameFilter) {
        querys.push({
          key: "name",
          filter: {
            _contains: nameFilter,
          },
        });
      }

      const res = await fetchCollectionsData<ProductTechfile>("product", {
        page: pageFilter,
        limit: 20,
        lang: lang.toString(),
        publish: true,
        fields: [
          "id",
          "slug",
          "name",
          "category.id",
          "category.name",
          "category.lang",
          "category.lang_data.lang",
          "category.lang_data.name",
          "datasheet1_text",
          "datasheet1_file.*",
          "datasheet2_text",
          "datasheet2_file.*",
          "datasheet3_text",
          "datasheet3_file.*",
          "application_file.*",
          "evm_file.*",
        ],
        querys,
        sort: "-category.id",
        searchLangData: true,
        excludeLangFields: ["slug"],
      });
      return res;
    }
  );

  const groupedData = useMemo(() => {
    if (!categoryFilters.length && !searchName.length) {
      return [];
    }

    const groups: {
      id: number;
      name: string;
      data: ProductTechfile[];
    }[] = [];
    result?.data?.map((el) => {
      const groupIndex = groups.findIndex(
        (group) => group.id === el.category?.id
      );
      if (groupIndex === -1) {
        groups.push({
          id: el.category?.id,
          name: getLangData("name", el.category?.name, el.category?.lang_data),
          data: [el],
        });
      } else {
        groups[groupIndex].data.push(el);
      }
    });

    return groups;
  }, [result, categoryFilters, searchName, getLangData]);

  const handleSearchName = useCallback(() => {
    setSearchName(name);
  }, [name]);

  useEffect(() => {
    if ((categoryFilters.length || searchName) && !userInfoLoading) {
      trigger();
    }
  }, [categoryFilters, searchName, page]);

  const downloadNoAuth = useCallback(() => {
    toast(t("登录下载提示"));
  }, []);

  const categorySelectors = useMemo(
    () => (
      <Accordion type="multiple">
        <AccordionItem className="border-none" value="documentCategory">
          <AccordionTrigger>
            <div className="flex items-center">
              <img
                className="mr-1.5 w-6 md:mr-2 md:w-7"
                src="/icon/ducoment.svg"
                alt="Icon Document"
              />
              {t("文档类别")}
            </div>
          </AccordionTrigger>
          <AccordionContent className="pl-2 md:pl-6">
            <CategorySelector
              data={[
                { id: 1, name: t("产品数据手册") },
                { id: 2, name: t("产品应用手册") },
                { id: 3, name: t("EVM用户指南") },
              ]}
              value={documentCategorys}
              onChange={setDocumentCategorys}
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem className="border-none" value="applicationCategory">
          <AccordionTrigger>
            <div className="flex items-center">
              <img
                className="mr-1.5 w-6 md:mr-2 md:w-7"
                src="/icon/category.svg"
                alt="Icon Category"
              />
              {t("应用类别")}
            </div>
          </AccordionTrigger>
          <AccordionContent className="pl-2 md:pl-6">
            <CategorySelector
              data={props.applicationCategoryData}
              value={applicationCategorys}
              onChange={setApplicationCategorys}
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem className="border-none" value="productCategory">
          <AccordionTrigger>
            <div className="flex items-center">
              <img
                className="mr-1.5 w-6 md:mr-2 md:w-7"
                src="/icon/product-category.svg"
                alt="Icon Category"
              />
              {t("产品类别")}
            </div>
          </AccordionTrigger>
          <AccordionContent className="pl-2 md:pl-6">
            <CategorySelector
              data={props.productCategoryData}
              value={productCategorys}
              onChange={setProductCategorys}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    ),
    [documentCategorys, productCategorys, applicationCategorys, props, t]
  );

  useEffect(() => {
    const listener = () => {
      setIsMobile(window.matchMedia("(max-width: 768px)").matches);
    };
    listener();
    window.addEventListener("resize", listener);
    return () => {
      window.removeEventListener("resize", listener);
    };
  }, []);

  return (
    <div className="w-full max-md:-ml-6 max-md:box-content max-md:bg-zinc-100 max-md:px-6 max-md:py-10">
      <Title title={t("资料下载中心")} underline={false} />
      <div className="mt-4 flex items-center justify-center gap-x-2 md:mt-3 md:gap-x-10">
        <div className="flex flex-wrap items-center justify-center gap-y-1 text-center text-sm md:text-lg">
          <img
            className="mr-1.5 w-3 md:mr-3 md:w-6"
            src="/icon/datasheet.svg"
            alt="Icon Dataseeht"
          />
          {t("产品数据手册")}
        </div>
        <hr className="h-3 border-l md:h-5" />
        <div className="flex flex-wrap items-center justify-center gap-y-1 text-center text-sm md:text-lg">
          <img
            className="mr-1.5 w-3 md:mr-3 md:w-6"
            src="/icon/product.svg"
            alt="Icon Dataseeht"
          />
          {t("产品应用手册")}
        </div>
        <hr className="h-3 border-l md:h-5" />
        <div className="flex flex-wrap items-center justify-center gap-y-1 text-center text-sm md:text-lg">
          <img
            className="mr-1.5 w-3 md:mr-3 md:w-6"
            src="/icon/evm.svg"
            alt="Icon Dataseeht"
          />
          {t("EVM用户指南")}
        </div>
      </div>
      <div className="group relative mt-10 w-full text-xs md:mt-16 md:text-2xl">
        <input
          type="text"
          className="w-full rounded-[19px] border py-2.5 pl-4 pr-10 outline-none placeholder:text-zinc-200 focus:border-primary group-hover:border-primary md:rounded-[33px] md:py-3.5 md:pl-11 md:pr-20"
          value={name}
          onChange={(e) => {
            setName(e.target.value)
            setPage(1);
          }}
          placeholder={t("搜索提示")}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSearchName();
            }
          }}
        />
        <Button
          onClick={handleSearchName}
          disabled={!name}
          className="absolute right-1 top-1/2 h-[30px] w-[30px] -translate-y-1/2 rounded-[15px] p-0 md:h-[56px] md:w-[56px] md:rounded-[30px]">
          <Search className="md:h-7 md:w-7" size={18} />
        </Button>
      </div>
      <hr className="my-14 border-t border-t-zinc-200 max-md:hidden" />
      <div className="relative flex items-start max-md:mt-5 max-md:flex-col max-md:gap-y-5 md:gap-x-12">
        {!isMobile && (
          <div className="roudned sticky top-20 h-auto w-72 shrink-0 rounded-xl bg-white py-7 pl-8 shadow-lg">
            <div className="max-h-[70vh] overflow-y-auto pr-6">
              {categorySelectors}
            </div>
          </div>
        )}
        {isMobile && (
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="destructive"
                className="sticky top-12 z-10 w-full rounded-xl bg-white py-3.5 text-sm shadow-lg">
                {t<any, any>("筛选条件-Count", {
                  count: categoryFilters.length,
                })}
              </Button>
            </DialogTrigger>
            <DialogPortal>
              <DialogOverlay className="bg-opacity-60" />
              <DialogContent className="flex h-[100vh] w-[100vw] flex-col gap-0 bg-white px-0 py-0">
                <DialogTitle className="border-b border-b-zinc-200 py-3 text-center text-lg font-normal">
                  {t("筛选条件")}
                </DialogTitle>
                <DialogClose className="absolute right-3 top-2 p-2">
                  <X size={20} />
                </DialogClose>
                <div className="mt-7 flex flex-auto flex-col px-6">
                  <div className="flex items-center justify-between border-b border-zinc-200 py-2">
                    <h4 className="text-lg font-bold">{t("类别")}</h4>
                    <Button variant="link" onClick={reset}>
                      <RotateCw className="mr-2 -rotate-45" size={18} />
                      {t("重置")}
                    </Button>
                  </div>
                  <div className="flex-auto overflow-y-auto py-5">
                    {categorySelectors}
                  </div>
                </div>
              </DialogContent>
            </DialogPortal>
          </Dialog>
        )}
        <div className="relative w-full md:flex-auto">
          <div
            className={cn("rounded-xl border px-5 py-4 md:px-10 md:py-6", {
              "max-md:hidden": !categoryFilters.length,
            })}>
            <div className="flex items-center text-sm md:text-lg">
              {t("所选筛选条件")}
              <hr className="mx-4 h-3 border-l md:mx-7 md:h-5" />
              <Button
                variant="ghost"
                className="px-0 text-sm hover:text-primary md:text-lg"
                onClick={resetAll}>
                <RotateCw className="mr-2 md:mr-3" size={18} />
                {t("重置所有")}
              </Button>
            </div>
            {(categoryFilters.length > 0 || !!searchName) && (
              <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 md:mt-8">
                {!!searchName && (
                  <span
                    className="flex cursor-pointer items-center rounded-md border bg-red-100 px-2.5 py-1 text-xs md:rounded-lg md:px-4 md:text-sm"
                    onClick={() => {
                      setSearchName("");
                      setName("");
                    }}>
                    {searchName}
                    <X className="ml-2" size={16} />
                  </span>
                )}
                {categoryFilters.map((el) => (
                  <span
                    key={`${el.type}_${el.id}`}
                    className="flex cursor-pointer items-center rounded-md border px-2.5 py-1 text-xs md:rounded-lg md:px-4 md:text-sm"
                    onClick={() => {
                      removeFilter(el.id, el.type);
                    }}>
                    {el.name}
                    <X className="ml-2" size={16} />
                  </span>
                ))}
              </div>
            )}
          </div>
          {!categoryFilters.length && !searchName && (
            <div className="mt-2.5 flex w-full flex-col items-center py-10 md:mt-10 md:py-20">
              <img
                className="w-40 md:w-[270px]"
                src="/no-filter.png"
                alt={t("无搜索条件提示")}
              />
              <span className="mt-3 text-sm text-gray md:mt-5">
                {t("无搜索条件提示")}
              </span>
            </div>
          )}
          {categoryFilters.length > 0 && !loading && !result?.count && (
            <div className="mt-2.5 flex w-full flex-col items-center py-10 md:mt-10 md:py-20">
              <img
                className="w-40 md:w-[270px]"
                src="/no-filter.png"
                alt={tCommon("未检索到相关数据")}
              />
              <span className="mt-3 text-sm text-gray md:mt-5">
                {tCommon("未检索到相关数据")}
              </span>
            </div>
          )}
          {groupedData.length > 0 && (
            <>
              {groupedData.map((group) => (
                <div key={group.id} className="mt-8 w-full md:mt-12 md:pl-9">
                  <div className="flex w-full items-center border-b border-zinc-400">
                    <img
                      className="mr-2 w-4 md:mr-5 md:w-6"
                      src="/icon/chip.svg"
                      alt="Icon Product Category"
                    />
                    <h4 className="py-3 text-base md:text-2xl">{group.name}</h4>
                  </div>
                  {group.data.map((el) => (
                    <div
                      key={el.id}
                      className="flex w-full justify-between border-b border-zinc-400 py-4 md:py-5">
                      <div className="mr-3 border-r pr-3 text-xs max-md:leading-[18px] md:mr-9 md:pr-9 md:text-lg">
                        {el.name}
                      </div>
                      <div className="flex flex-wrap gap-x-2 gap-y-3 md:gap-x-10">
                        {(showDataseeht || documentCategorys.length === 0) && (
                          <>
                            {/** 未登录用户 Datasheet只展示不允许下载 */}
                            {!user?.role?.level && el.datasheet1_file && (
                              <div className="flex items-center text-xs md:text-sm">
                                {t("产品数据手册")}
                                <RouterLink
                                  className="ml-2 flex cursor-pointer items-center text-primary md:ml-3"
                                  onClick={downloadNoAuth}>
                                  <span className="mr-1 uppercase max-md:hidden">
                                    {getSuffixByFileName(
                                      el.datasheet1_file.filename_disk
                                    )}
                                  </span>
                                  <Download size={16} />
                                </RouterLink>
                              </div>
                            )}
                            {/** 登录用户 Datasheet展示对应权限等级的 */}
                            {user?.role.level &&
                              el[
                                `datasheet${user.role.level}_file` as "datasheet1_file"
                              ] && (
                                <div className="flex items-center text-xs md:text-sm">
                                  {el[
                                    `datasheet${user.role.level}_text` as "datasheet1_text"
                                  ] ?? t("产品数据手册")}
                                  <RouterLink
                                    className="ml-2 flex items-center text-primary md:ml-3"
                                    href={getFileDownloadAdress(
                                      `${
                                        el[
                                          `datasheet${user.role.level}_file` as "datasheet1_file"
                                        ]?.id || ""
                                      }/${
                                        el[
                                          `datasheet${user.role.level}_file` as "datasheet1_file"
                                        ]?.filename_download || ""
                                      }`
                                    )}
                                    target="_blank"
                                    download={
                                      el[
                                        `datasheet${user.role.level}_file` as "datasheet1_file"
                                      ]?.filename_download
                                    }>
                                    <span className="mr-1 uppercase max-md:hidden">
                                      {getSuffixByFileName(
                                        el[
                                          `datasheet${user.role.level}_file` as "datasheet1_file"
                                        ]?.filename_disk || ""
                                      )}
                                    </span>
                                    <Download size={16} />
                                  </RouterLink>
                                </div>
                              )}
                          </>
                        )}
                        {(showApplication || documentCategorys.length === 0) &&
                          el.application_file && (
                            <div className="flex items-center text-xs md:text-sm">
                              {t("产品应用手册")}
                              {!user ? 
                                <RouterLink
                                  className="ml-2 flex items-center text-primary md:ml-3"
                                  onClick={downloadNoAuth}
                                >
                                  <span className="mr-1 uppercase max-md:hidden">
                                    {getSuffixByFileName(
                                      el.application_file.filename_disk
                                    )}
                                  </span>
                                  <Download size={16} />
                                </RouterLink> :
                                <RouterLink
                                  className="ml-2 flex items-center text-primary md:ml-3"
                                  href={getFileDownloadAdress(
                                    `${el.application_file?.id}/${el.application_file?.filename_download}`
                                  )}
                                  target="_blank"
                                  download={
                                    el.application_file.filename_download
                                  }
                                >
                                  <span className="mr-1 uppercase max-md:hidden">
                                    {getSuffixByFileName(
                                      el.application_file.filename_disk
                                    )}
                                  </span>
                                  <Download size={16} />
                                </RouterLink>
                              }
                            </div>
                          )}
                        {(showEvm || documentCategorys.length === 0) &&
                          el.evm_file && (
                            <div className="flex items-center text-xs md:text-sm">
                              {t("EVM用户指南")}
                              {!user ?
                                <RouterLink
                                  className="ml-2 flex items-center text-primary md:ml-3"
                                  onClick={downloadNoAuth}
                                >
                                  <span className="mr-1 uppercase max-md:hidden">
                                    {getSuffixByFileName(
                                      el.evm_file.filename_disk
                                    )}
                                  </span>
                                  <Download size={16} />
                                </RouterLink> :
                                <RouterLink
                                  className="ml-2 flex items-center text-primary md:ml-3"
                                  href={getFileDownloadAdress(
                                    `${el.evm_file.id}/${el.evm_file.filename_download}`
                                  )}
                                  target="_blank"
                                  download={el.evm_file.filename_download}
                                >
                                  <span className="mr-1 uppercase max-md:hidden">
                                    {getSuffixByFileName(
                                      el.evm_file.filename_disk
                                    )}
                                  </span>
                                  <Download size={16} />
                                </RouterLink>
                              }
                            </div>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
              <Pagination
                className="mt-5 md:mt-16"
                current={page}
                onChange={setPage}
                total={result?.count ?? 0}
                pageSize={20}
              />
            </>
          )}
        </div>
        {loading && (
          <Loading
            loading
            className="fixed left-0 top-0 h-[100vh] w-[100vw]"
            hideText
          />
        )}
      </div>
    </div>
  );
}
